import { useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import type { SortDirection } from '~/api/types';
import type { DepartmentTableColumnKey } from '~/components/table/department/types';
import { USER_ROLES } from '~/features/auth/roles';
import { UserAdminDepartmentsSearchParamsProvider } from '~/features/userAdministration/departments/params';
import type {
  UserAdminDepartmentsSearchParams,
  UserAdminDepartmentsSearchParamsHandler,
} from '~/features/userAdministration/departments/types';
import { UserAdminDepartmentsScreen } from '~/features/userAdministration/departments/UserAdminDepartmentsScreen';
import { protectedRoute } from '~/utils/routesAuthorization';

export const Route = createFileRoute('/user-administration/departments/')({
  component: RouteComponent,
  validateSearch: (search): UserAdminDepartmentsSearchParams => {
    // Validate and parse the search params into a typed state
    const defaultSortDir: SortDirection =
      search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort: (search.sort as DepartmentTableColumnKey | undefined) ?? undefined,
      search:
        search.search && typeof search.search === 'string'
          ? decodeURI(search.search)
          : undefined,
      sortDir,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.HcpAdmin, USER_ROLES.NonHcpAdmin],
    });
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();

  const onSearchParamsChange: UserAdminDepartmentsSearchParamsHandler =
    useCallback((onSearch) => navigate({ search: onSearch }), [navigate]);

  return (
    <UserAdminDepartmentsSearchParamsProvider
      params={Route.useSearch()}
      onSearchParamsChange={onSearchParamsChange}
    >
      <UserAdminDepartmentsScreen />
    </UserAdminDepartmentsSearchParamsProvider>
  );
}
